import React from "react";
import { Sparkles, Plus, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { toast } from "sonner";

export function AiExtractPanel({
  aiExtractText,
  setAiExtractText,
  aiExtracting,
  aiResults,
  setAiResults,
  onExtract,
  formData,
  setFormData,
}) {
  const scope = formData.contract_scope || "";
  // Use scope as the extract source, fall back to content summary
  const extractSource = scope || aiExtractText;

  const handleExtract = () => {
    if (scope && !aiExtractText) {
      setAiExtractText(scope);
    }
    onExtract();
  };

  // Auto-set aiExtractText from scope when scope exists and text is empty
  React.useEffect(() => {
    if (scope && !aiExtractText) {
      setAiExtractText(scope);
    }
  }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card className="border-cyan-200 dark:border-cyan-800 bg-cyan-50/30 dark:bg-cyan-950/10">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-cyan-600" />
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-cyan-700 dark:text-cyan-400">
            AI Assistant — Extract Deliverables & KPIs
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {scope ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">
              AI will use the contract scope to extract deliverables and KPIs, and suggest additional ones based on industry best practices.
            </p>
            <div className="bg-white dark:bg-slate-800 border rounded-sm p-2.5 text-xs text-slate-600 dark:text-slate-400 line-clamp-3">
              {scope}
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-500">
            No scope defined. Define the contract scope in the placeholder review panel above, or the AI will use the contract content.
          </p>
        )}
        <div className="flex items-center gap-3">
          <Button
            onClick={handleExtract}
            disabled={aiExtracting || !extractSource.trim()}
            className="gap-2 rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white"
            size="sm"
            data-testid="ai-extract-btn"
          >
            {aiExtracting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {aiExtracting ? "Extracting..." : "Extract & Suggest"}
          </Button>
          {aiResults && (
            <span className="text-xs text-slate-500">
              Found {aiResults.deliverables?.length || 0} deliverables, {aiResults.kpis?.length || 0} KPIs
            </span>
          )}
        </div>

        {/* AI Results */}
        {aiResults && (
          <div className="space-y-4 pt-3 border-t border-cyan-200 dark:border-cyan-800">
            {aiResults.deliverables?.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-mono uppercase text-slate-500">Extracted / Suggested Deliverables</Label>
                  <Button
                    variant="ghost" size="sm" className="h-6 text-xs text-cyan-600"
                    onClick={() => {
                      const newItems = aiResults.deliverables.map((d) => ({
                        id: d.id, description: d.description, delivery_date: d.delivery_date || "",
                      }));
                      setFormData((prev) => ({ ...prev, deliverables: [...prev.deliverables, ...newItems] }));
                      toast.success(`Added ${newItems.length} deliverables`);
                    }}
                    data-testid="add-all-ai-deliverables-btn"
                  >
                    Add All
                  </Button>
                </div>
                {aiResults.deliverables.map((d, idx) => (
                  <div key={d.id} className="flex items-center gap-2 p-2 bg-white dark:bg-slate-800 border rounded-sm text-sm">
                    <Badge variant="outline" className={`text-[10px] shrink-0 ${d.source === "extracted" ? "border-blue-300 text-blue-600" : "border-amber-300 text-amber-600"}`}>
                      {d.source === "extracted" ? "Extracted" : "Suggested"}
                    </Badge>
                    <span className="flex-1 text-slate-700 dark:text-slate-300">{d.description}</span>
                    {d.delivery_date && <span className="text-xs text-slate-400 shrink-0">{d.delivery_date}</span>}
                    <Button
                      variant="ghost" size="sm" className="h-6 w-6 p-0 text-cyan-600 hover:text-cyan-700 shrink-0"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          deliverables: [...prev.deliverables, { id: d.id, description: d.description, delivery_date: d.delivery_date || "" }],
                        }));
                        setAiResults((prev) => ({ ...prev, deliverables: prev.deliverables.filter((_, i) => i !== idx) }));
                        toast.success("Deliverable added");
                      }}
                      data-testid={`add-ai-deliverable-${idx}`}
                    >
                      <Plus size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {aiResults.kpis?.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-mono uppercase text-slate-500">Extracted / Suggested KPIs</Label>
                  <Button
                    variant="ghost" size="sm" className="h-6 text-xs text-cyan-600"
                    onClick={() => {
                      const newItems = aiResults.kpis.map((k) => ({
                        id: k.id, description: k.description, measure: k.measure || "",
                      }));
                      setFormData((prev) => ({ ...prev, kpis: [...prev.kpis, ...newItems] }));
                      toast.success(`Added ${newItems.length} KPIs`);
                    }}
                    data-testid="add-all-ai-kpis-btn"
                  >
                    Add All
                  </Button>
                </div>
                {aiResults.kpis.map((k, idx) => (
                  <div key={k.id} className="p-3 bg-white dark:bg-slate-800 border rounded-sm text-sm" data-testid={`ai-kpi-row-${idx}`}>
                    <div className="flex items-start gap-2">
                      <Badge variant="outline" className={`text-[10px] shrink-0 mt-0.5 ${k.source === "extracted" ? "border-blue-300 text-blue-600" : "border-amber-300 text-amber-600"}`}>
                        {k.source === "extracted" ? "Extracted" : "Suggested"}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-700 dark:text-slate-300 break-words">{k.description}</p>
                        {k.measure && <p className="text-xs text-slate-400 font-mono mt-1 break-words">{k.measure}</p>}
                      </div>
                      <Button
                        variant="ghost" size="sm" className="h-6 w-6 p-0 text-cyan-600 hover:text-cyan-700 shrink-0"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            kpis: [...prev.kpis, { id: k.id, description: k.description, measure: k.measure || "" }],
                          }));
                          setAiResults((prev) => ({ ...prev, kpis: prev.kpis.filter((_, i) => i !== idx) }));
                          toast.success("KPI added");
                        }}
                        data-testid={`add-ai-kpi-${idx}`}
                      >
                        <Plus size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
